import { Check, ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MISSING_SENTINEL,
  emptyFilter,
  isFilterActive,
  type BooleanFilter,
  type FilterMap,
  type FilterValue,
  type PcaField,
} from "./pca-filter-types";

type Props = {
  fields: PcaField[];
  filters: FilterMap;
  onChange: (filters: FilterMap) => void;
};

export function PcaFilters({ fields, filters, onChange }: Props) {
  if (fields.length === 0) return null;

  const update = (name: string, value: FilterValue) => {
    onChange({ ...filters, [name]: value });
  };

  return (
    <>
      {fields.map((field) => {
        const f = filters[field.name] ?? emptyFilter(field);
        return (
          <FieldChip
            key={field.name}
            field={field}
            filter={f}
            onChange={(v) => update(field.name, v)}
            onClear={() => update(field.name, emptyFilter(field))}
          />
        );
      })}
    </>
  );
}

export function FilterChip({
  label,
  active,
  onClear,
  children,
}: {
  label: string;
  active?: boolean;
  onClear?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`group inline-flex h-10 items-center rounded-md border bg-card pl-3 pr-2 shadow-sm transition-colors ${
        active ? "border-primary/40 ring-1 ring-primary/15" : "border-border/60"
      }`}
    >
      <span
        className={`mr-2.5 text-[11px] font-medium uppercase leading-none tracking-wider ${
          active ? "text-primary" : "text-muted-foreground"
        }`}
      >
        {label}
      </span>
      <div className="flex h-full items-center gap-2 border-l border-border/60 pl-2.5">
        {children}
      </div>
      {active && onClear && (
        <Button
          variant="ghost"
          size="icon"
          className="ml-1 h-6 w-6 text-muted-foreground hover:text-destructive"
          onClick={onClear}
          aria-label={`Clear ${label} filter`}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

function FieldChip({
  field,
  filter,
  onChange,
  onClear,
}: {
  field: PcaField;
  filter: FilterValue;
  onChange: (v: FilterValue) => void;
  onClear: () => void;
}) {
  const active = isFilterActive(filter);

  if (field.type === "string" && filter.type === "string") {
    return (
      <FilterChip label={field.name} active={active} onClear={onClear}>
        <Input
          value={filter.value}
          onChange={(e) => onChange({ type: "string", value: e.target.value })}
          placeholder="contains…"
          className="h-7 w-40 border-0 bg-transparent px-0 text-sm leading-none shadow-none placeholder:text-muted-foreground/70 focus-visible:ring-0"
        />
      </FilterChip>
    );
  }

  if (field.type === "number" && filter.type === "number") {
    return (
      <FilterChip label={field.name} active={active} onClear={onClear}>
        <Input
          value={filter.min}
          onChange={(e) => onChange({ ...filter, min: e.target.value })}
          placeholder="min"
          type="number"
          className="h-7 w-14 border-0 bg-transparent px-0 text-center text-sm leading-none tabular-nums shadow-none placeholder:text-muted-foreground/70 focus-visible:ring-0"
        />
        <span className="text-muted-foreground">–</span>
        <Input
          value={filter.max}
          onChange={(e) => onChange({ ...filter, max: e.target.value })}
          placeholder="max"
          type="number"
          className="h-7 w-14 border-0 bg-transparent px-0 text-center text-sm leading-none tabular-nums shadow-none placeholder:text-muted-foreground/70 focus-visible:ring-0"
        />
      </FilterChip>
    );
  }

  if (field.type === "boolean" && filter.type === "boolean") {
    return (
      <FilterChip label={field.name} active={active} onClear={onClear}>
        <Select
          value={filter.value}
          onValueChange={(v) =>
            onChange({ type: "boolean", value: v as BooleanFilter })
          }
        >
          <SelectTrigger className="h-7 w-[5.5rem] gap-2 border-0 bg-transparent px-0 text-sm leading-none shadow-none focus:ring-0 focus-visible:ring-0 [&>span]:truncate">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any</SelectItem>
            <SelectItem value="true">True</SelectItem>
            <SelectItem value="false">False</SelectItem>
            <SelectItem value="missing">Missing</SelectItem>
          </SelectContent>
        </Select>
      </FilterChip>
    );
  }

  if (field.type === "enum" && filter.type === "enum") {
    const choices = field.choices ?? [];
    const selected = filter.values;
    const toggle = (val: string) => {
      const next = selected.includes(val)
        ? selected.filter((v) => v !== val)
        : [...selected, val];
      onChange({ type: "enum", values: next });
    };
    return (
      <FilterChip label={field.name} active={active} onClear={onClear}>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex h-7 items-center gap-2 rounded-sm px-0 text-sm leading-none text-foreground hover:text-primary"
            >
              {selected.length === 0 ? (
                <span className="text-muted-foreground">Any</span>
              ) : (
                <>
                  <Badge
                    variant="secondary"
                    className="h-5 px-1.5 text-[10px] font-semibold"
                  >
                    {selected.length}
                  </Badge>
                  <span className="text-muted-foreground">selected</span>
                </>
              )}
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-0" align="start">
            <Command>
              <CommandInput placeholder={`Filter ${field.name}…`} />
              <CommandList>
                <CommandEmpty>No choices</CommandEmpty>
                <CommandGroup>
                  {choices.map((c) => (
                    <CommandItem key={c} onSelect={() => toggle(c)}>
                      <Check
                        className={`mr-2 h-4 w-4 ${
                          selected.includes(c) ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      {c}
                    </CommandItem>
                  ))}
                  <CommandItem onSelect={() => toggle(MISSING_SENTINEL)}>
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        selected.includes(MISSING_SENTINEL)
                          ? "opacity-100"
                          : "opacity-0"
                      }`}
                    />
                    <span className="italic text-muted-foreground">
                      (missing)
                    </span>
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </FilterChip>
    );
  }

  return null;
}
